'use client'
/**
 * Voice call controller (WebRTC, peer-to-peer)
 * ─────────────────────────────────────────────────────────────────────────────
 * Owns the microphone, the RTCPeerConnection and the call state machine. All
 * signalling rides the existing chat WebSocket; audio never touches the server.
 *
 * State:  idle → calling → connecting → active → idle
 *         idle → incoming → connecting → active → idle
 *
 * Subscribe with onCallState(fn); drive it with start/accept/reject/hangup.
 *
 * Two things this deliberately handles, because they are the usual sources of
 * one-way audio and stuck calls:
 *   - ICE candidates that arrive before the remote description is set are
 *     queued and flushed, rather than thrown away.
 *   - Every exit path runs through cleanup(), so the microphone track is always
 *     stopped and the browser's recording indicator always goes away.
 */
import PubSub from 'pubsub-js'
import { chatActions } from './chatSocketClient'
import * as tones from './callTones'

const STATE_TOPIC = 'chat:call_state'

const FALLBACK_ICE = [{ urls: ['stun:stun.l.google.com:19302'] }]

let pc = null
let localStream = null
let remoteAudio = null
let pendingIce = []          // inbound candidates waiting for setRemoteDescription
let outboundIce = []         // outbound candidates gathered before the call id exists
let ringTimer = null

// Fetched once per socket connection. The caller has to build its offer before the
// server has created the call, so without a cached copy it would create the peer
// connection with no TURN — and a connection cannot gain a relay after the fact.
let iceConfig = null
export const setIceConfig = servers => { if (Array.isArray(servers) && servers.length) iceConfig = servers }
const currentIce = () => iceConfig || FALLBACK_ICE

let state = { status: 'idle' }

const emit = patch => {
  state = { ...state, ...patch }
  PubSub.publish(STATE_TOPIC, state)
}

export const onCallState = fn => PubSub.subscribe(STATE_TOPIC, (_t, s) => fn(s))
export const offCallState = token => PubSub.unsubscribe(token)
export const getCallState = () => state

// ── media ────────────────────────────────────────────────────────────────────
// navigator.mediaDevices is undefined outside a secure context, which is far more
// often the cause than an old browser — so name the actual origin rather than
// blaming the browser and sending someone to reinstall Chrome.
function secureContextHint() {
  if (typeof window === 'undefined') return ''
  const { protocol, hostname, host } = window.location
  const localhost = hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1'
  if (protocol !== 'https:' && !localhost) {
    return `Calls need a secure origin. This page is on http://${host} — open it over HTTPS, or use http://localhost:${window.location.port || 80} on this machine.`
  }
  return ''
}

async function getMic() {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error(secureContextHint() || 'This browser cannot access the microphone.')
  }
  try {
    return await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      video: false,
    })
  } catch (e) {
    // getUserMedia's error names are more useful than its messages.
    if (e.name === 'NotAllowedError') throw new Error('Microphone permission was blocked. Allow it in the browser address bar and try again.')
    if (e.name === 'NotFoundError') throw new Error('No microphone found.')
    if (e.name === 'NotReadableError') throw new Error('The microphone is in use by another application.')
    throw new Error('Could not open the microphone.')
  }
}

function ensureRemoteAudio() {
  if (remoteAudio) return remoteAudio
  remoteAudio = new Audio()
  remoteAudio.autoplay = true
  remoteAudio.playsInline = true
  return remoteAudio
}

// ── output routing: earpiece vs loudspeaker ──────────────────────────────────
/*
 * Two mechanisms, because no single one works everywhere.
 *
 * On desktop, setSinkId names a real output device and playback stays on the
 * plain element, so the browser's echo canceller keeps its reference signal.
 *
 * On Android and iOS setSinkId does not exist at all — there is no device to
 * pick. What does move the audio there is the playback path itself: a plain
 * element alongside an open microphone is treated as a call and goes to the
 * earpiece, while audio played through an AudioContext is treated as media and
 * goes to the loudspeaker. That is not a trick; it is precisely the behaviour
 * that caused the echo bug this toggle grew out of, used on purpose rather than
 * by accident.
 *
 * The cost is real: the loudspeaker path loses the echo canceller's reference,
 * so speaker mode can echo — as speakerphone does. Hence earpiece by default.
 */
const SPEAKER_LABEL = /speaker|internal|built[\s-]?in|loud/i
const WORN_LABEL = /head(set|phone)|bluetooth|earpiece|hands[\s-]?free|airpod|buds|hearing/i

let routeCtx = null    // AudioContext used only for the loudspeaker path
let routeSrc = null    // remote stream -> that context
let speakerOn = false

const canPickDevice = () =>
  typeof HTMLMediaElement !== 'undefined' &&
  typeof HTMLMediaElement.prototype.setSinkId === 'function' &&
  typeof navigator !== 'undefined' &&
  Boolean(navigator.mediaDevices?.enumerateDevices)

const AudioCtor = () =>
  (typeof window === 'undefined' ? null : window.AudioContext || window.webkitAudioContext)

const canForceSpeaker = () => Boolean(AudioCtor())

export const canToggleSpeaker = () => canPickDevice() || canForceSpeaker()

/** Desktop: choose a real output device on the element. */
async function pickDevice(wantSpeaker) {
  if (!wantSpeaker) return remoteAudio.setSinkId('')      // back to system default
  const devices = await navigator.mediaDevices.enumerateDevices()
  const pick = devices.find(d =>
    d.kind === 'audiooutput' && d.label && SPEAKER_LABEL.test(d.label) && !WORN_LABEL.test(d.label))
  // Nothing speaker-like is attached, so leave the route alone rather than
  // switching to an arbitrary device.
  if (!pick) return null
  return remoteAudio.setSinkId(pick.deviceId)
}

/** Phone: move playback onto an AudioContext, which lands on the loudspeaker. */
function forceSpeaker(on) {
  const Ctor = AudioCtor()
  if (!on) {
    releaseSpeakerGraph()
    if (remoteAudio) remoteAudio.muted = false
    return
  }
  const stream = remoteAudio?.srcObject
  if (!stream || !Ctor) return
  if (!routeCtx) routeCtx = new Ctor()
  if (routeCtx.state === 'suspended') routeCtx.resume().catch(() => {})
  if (!routeSrc) {
    routeSrc = routeCtx.createMediaStreamSource(stream)
    routeSrc.connect(routeCtx.destination)
  }
  // Muted rather than paused: pausing can drop the very stream the context is
  // reading from, which would silence both paths at once.
  remoteAudio.muted = true
}

function releaseSpeakerGraph() {
  if (routeSrc) { try { routeSrc.disconnect() } catch (_) {} routeSrc = null }
  if (routeCtx) { try { routeCtx.close() } catch (_) {} routeCtx = null }
}

/** Put the current choice into effect. No-op until there is audio to route. */
function applyRoute() {
  if (!remoteAudio) return
  if (canPickDevice()) pickDevice(speakerOn).catch(() => {})
  else if (canForceSpeaker()) forceSpeaker(speakerOn)
}

/*
 * Recorded even with no audio yet: the control is offered from the moment the
 * call is ringing, and a choice made then has to survive until the remote track
 * arrives — otherwise tapping speaker while it rings would light the button up
 * and change nothing once someone answered.
 */
export function setSpeaker(on) {
  speakerOn = Boolean(on)
  applyRoute()
  emit({ speakerOn })
  return speakerOn
}

export const toggleSpeaker = () => setSpeaker(!speakerOn)

// ── peer connection ──────────────────────────────────────────────────────────
function createPeer(iceServers, callId) {
  const conn = new RTCPeerConnection({ iceServers: iceServers?.length ? iceServers : FALLBACK_ICE })

  // The caller starts gathering before the server has issued a call id. Buffer
  // those candidates instead of sending them with a null id, where the server
  // would drop them — losing the host candidates that connect most calls.
  conn.onicecandidate = e => {
    if (!e.candidate) return
    const json = e.candidate.toJSON()
    const id = state.call?.callId || callId
    if (id) chatActions.callIce(id, json)
    else outboundIce.push(json)
  }

  conn.ontrack = e => {
    const el = ensureRemoteAudio()
    if (el.srcObject !== e.streams[0]) el.srcObject = e.streams[0]
    // Honour a speaker choice made while it was still ringing.
    if (speakerOn) applyRoute()
    // Autoplay can still be refused; surfaced so the UI can prompt a tap.
    el.play?.().catch(() => emit({ needsPlayTap: true }))
  }

  conn.onconnectionstatechange = () => {
    if (conn.connectionState === 'connected') {
      clearTimeout(ringTimer)
      // Full release, not just stop(): a live call must own the audio route, or
      // the remote voice plays out of the loudspeaker and echoes back in.
      tones.releaseAudio()
      tones.vibrateRing(false)
      emit({
        status: 'active',
        since: state.since || Date.now(),
        needsPlayTap: state.needsPlayTap,
        canToggleSpeaker: canToggleSpeaker(),
        speakerOn,
      })
    } else if (conn.connectionState === 'failed') {
      // No candidate pair worked — almost always missing TURN behind a firewall.
      finish('ice-failed', 'Could not connect. The network may be blocking calls.')
    }
  }

  return conn
}

async function flushPendingIce() {
  if (!pc || !pc.remoteDescription) return
  const queued = pendingIce
  pendingIce = []
  for (const c of queued) {
    try { await pc.addIceCandidate(c) } catch (_) { /* stale candidate, ignore */ }
  }
}

// ── teardown ─────────────────────────────────────────────────────────────────
function cleanup() {
  tones.stop()
  tones.vibrateRing(false)
  // A new call starts on the earpiece; it must not inherit the last one's route.
  releaseSpeakerGraph()
  speakerOn = false
  if (remoteAudio) remoteAudio.muted = false
  clearTimeout(ringTimer)
  ringTimer = null
  pendingIce = []
  outboundIce = []
  if (pc) {
    pc.onicecandidate = null
    pc.ontrack = null
    pc.onconnectionstatechange = null
    try { pc.close() } catch (_) {}
    pc = null
  }
  if (localStream) {
    localStream.getTracks().forEach(t => { try { t.stop() } catch (_) {} })
    localStream = null
  }
  if (remoteAudio) {
    try { remoteAudio.pause(); remoteAudio.srcObject = null } catch (_) {}
  }
}

// Single exit point, so the mic is released no matter how a call ends.
function finish(reason, message) {
  const callId = state.call?.callId
  const wasLive = state.status !== 'idle'
  cleanup()
  if (wasLive) tones.playEndTone()
  if (callId && reason && reason !== 'remote') chatActions.callEnd(callId, reason)
  state = { status: 'idle' }
  PubSub.publish(STATE_TOPIC, message ? { ...state, message } : state)
}

// ── outbound ─────────────────────────────────────────────────────────────────
export async function startCall(conversationId, peerName = '') {
  if (state.status !== 'idle') return
  emit({ status: 'calling', peerName, outgoing: true, message: '' })
  try {
    localStream = await getMic()
    // Built with the cached ICE config so TURN is available from the first
    // gather; the call id arrives later and flushes the buffered candidates.
    pc = createPeer(currentIce(), null)
    localStream.getTracks().forEach(t => pc.addTrack(t, localStream))
    const offer = await pc.createOffer({ offerToReceiveAudio: true })
    await pc.setLocalDescription(offer)
    chatActions.callStart(conversationId, offer.sdp)
  } catch (e) {
    cleanup()
    state = { status: 'idle' }
    PubSub.publish(STATE_TOPIC, { ...state, message: e.message })
  }
}

// ── inbound ──────────────────────────────────────────────────────────────────
export async function acceptCall() {
  const incoming = state.call
  const offerSdp = state.offerSdp
  if (state.status !== 'incoming' || !incoming || !offerSdp) return
  tones.stop()
  tones.vibrateRing(false)
  emit({ status: 'connecting' })
  try {
    localStream = await getMic()
    pc = createPeer(state.iceServers?.length ? state.iceServers : currentIce(), incoming.callId)
    localStream.getTracks().forEach(t => pc.addTrack(t, localStream))
    await pc.setRemoteDescription({ type: 'offer', sdp: offerSdp })
    await flushPendingIce()
    const answer = await pc.createAnswer()
    await pc.setLocalDescription(answer)
    chatActions.callAnswer(incoming.callId, answer.sdp)
    emit({ since: Date.now() })
  } catch (e) {
    finish('hangup', e.message)
  }
}

export function rejectCall() {
  const callId = state.call?.callId
  cleanup()
  if (callId) chatActions.callReject(callId)
  state = { status: 'idle' }
  PubSub.publish(STATE_TOPIC, state)
}

export function hangUp() {
  finish('hangup')
}

// ── controls ─────────────────────────────────────────────────────────────────
export function toggleMute() {
  if (!localStream) return false
  const track = localStream.getAudioTracks()[0]
  if (!track) return false
  track.enabled = !track.enabled
  emit({ muted: !track.enabled })
  return !track.enabled
}

// Retry playback after the browser refused autoplay; must be called from a click.
export function resumeAudio() {
  remoteAudio?.play?.().then(() => emit({ needsPlayTap: false })).catch(() => {})
}

// ── signalling from the socket ───────────────────────────────────────────────
export function handleCallSignal(event) {
  switch (event.type) {
    // Our offer was accepted by the server and is now ringing the other side.
    case 'call_config_v4':
      setIceConfig(event.iceServers)
      break

    case 'call_ringing_v4': {
      if (state.status !== 'calling' || !pc) return
      setIceConfig(event.iceServers)
      tones.startRingback()
      emit({ call: event.call, iceServers: event.iceServers })
      // Now that the call has an id, release everything gathered while it did not.
      const queued = outboundIce
      outboundIce = []
      queued.forEach(c => chatActions.callIce(event.call.callId, c))
      ringTimer = setTimeout(() => finish('timeout', 'No answer.'), event.ringTimeoutMs || 45000)
      break
    }

    case 'call_incoming_v4': {
      // Already busy locally — decline immediately rather than showing a second ring.
      if (state.status !== 'idle') { chatActions.callReject(event.call.callId); return }
      state = {
        status: 'incoming',
        call: event.call,
        offerSdp: event.sdp,
        iceServers: event.iceServers,
        peerName: event.from?.displayName || event.from?.code || 'Unknown',
        outgoing: false,
        toneBlocked: tones.isBlocked(),
      }
      tones.startRingtone()
      tones.vibrateRing(true)
      PubSub.publish(STATE_TOPIC, state)
      ringTimer = setTimeout(() => { if (state.status === 'incoming') rejectCall() }, event.ringTimeoutMs || 45000)
      break
    }

    case 'call_answered_v4': {
      if (!pc || state.status !== 'calling') return
      clearTimeout(ringTimer)
      tones.stop()
      emit({ status: 'connecting', since: Date.now() })
      pc.setRemoteDescription({ type: 'answer', sdp: event.sdp })
        .then(flushPendingIce)
        .catch(() => finish('ice-failed', 'Could not start the call.'))
      break
    }

    case 'call_ice_v4': {
      if (!event.candidate) return
      const candidate = new RTCIceCandidate(event.candidate)
      // Candidates routinely arrive before setRemoteDescription; queue them.
      if (!pc || !pc.remoteDescription) { pendingIce.push(candidate); return }
      pc.addIceCandidate(candidate).catch(() => {})
      break
    }

    case 'call_unavailable_v4': {
      finish(null, event.reason === 'busy' ? 'They are on another call.' : 'They are offline.')
      break
    }

    case 'call_ended_v4': {
      if (state.status === 'idle') return
      const wasRinging = state.status === 'calling' || state.status === 'incoming'
      const reason = event.call?.endReason
      let message = ''
      if (reason === 'declined') message = 'Call declined.'
      // A support desk rings everyone on it at once, so most agents will see
      // their ring stop because a colleague got there first. Saying so stops it
      // reading as a dropped call.
      else if (reason === 'answered-elsewhere') message = 'Answered by another agent.'
      else if (wasRinging && reason === 'timeout') message = 'No answer.'
      else if (reason === 'disconnected') message = 'The other side disconnected.'
      finish('remote', message)
      break
    }

    default:
      break
  }
}

export const callActions = {
  startCall, acceptCall, rejectCall, hangUp, toggleMute, resumeAudio,
  toggleSpeaker, setSpeaker, canToggleSpeaker,
  handleCallSignal, onCallState, offCallState, getCallState,
}
